import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import axios from "axios";
import {
  Activity,
  Heart,
  Grid3x3,
  KeyRound,
  CheckCircle2,
  XCircle,
  Loader2,
  ArrowUpRight,
} from "lucide-react";
import { Link } from "react-router-dom";

const StatCard = ({ label, value, icon: Icon, testId, hint, tone = "default" }) => (
  <div
    data-testid={testId}
    className="bg-card border border-border rounded-2xl p-6 hover:shadow-lg hover:-translate-y-0.5 transition-all"
  >
    <div className="flex items-start justify-between mb-4">
      <div
        className={`w-10 h-10 rounded-xl flex items-center justify-center ${
          tone === "primary"
            ? "bg-primary text-primary-foreground"
            : tone === "success"
            ? "bg-emerald-100 text-emerald-700"
            : tone === "warning"
            ? "bg-amber-100 text-amber-700"
            : "bg-secondary text-secondary-foreground"
        }`}
      >
        <Icon className="w-5 h-5" strokeWidth={2.2} />
      </div>
    </div>
    <div className="text-3xl font-extrabold tracking-tight leading-none mb-1.5">
      {value}
    </div>
    <div className="text-sm font-medium text-muted-foreground">{label}</div>
    {hint && <div className="text-xs text-muted-foreground mt-2">{hint}</div>}
  </div>
);

export default function Dashboard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        // /status is public
        const { data } = await axios.get(`${API}/status`);
        setStatus(data);
      } catch (e) {
        setStatus({ error: true });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="p-6 lg:p-10 max-w-6xl">
      <div className="mb-8 animate-in-up">
        <div className="text-xs font-bold uppercase tracking-widest text-primary mb-2">
          Overview
        </div>
        <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight mb-3">
          Command your <span className="text-primary">NearMe</span> app
        </h1>
        <p className="text-base text-muted-foreground max-w-2xl">
          Monitor API health, manage advertisements, curate favorites and test the Places
          integration — all from a single console.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading system status…
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 stagger mb-10">
            <StatCard
              testId="stat-api"
              label="Backend API"
              value={status?.api === "ok" ? "Online" : "Down"}
              icon={Activity}
              tone={status?.api === "ok" ? "success" : "warning"}
              hint="FastAPI /api endpoints"
            />
            <StatCard
              testId="stat-google"
              label="Google Places"
              value={status?.google_places_configured ? "Configured" : "Missing"}
              icon={KeyRound}
              tone={status?.google_places_configured ? "success" : "warning"}
              hint={
                status?.google_places_configured
                  ? "API key present"
                  : "Set GOOGLE_MAPS_API_KEY"
              }
            />
            <StatCard
              testId="stat-favorites"
              label="Favorites"
              value={status?.favorites_count ?? 0}
              icon={Heart}
              tone="primary"
              hint="Saved by users"
            />
            <StatCard
              testId="stat-categories"
              label="Categories"
              value={status?.categories_count ?? 0}
              icon={Grid3x3}
              hint="Available place types"
            />
          </div>

          {/* Quick actions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
            <QuickCard
              to="/ads"
              testId="quick-ads"
              title="Ad Configuration"
              desc="Tune banners, interstitials and rewarded video ads for every placement."
              tone="primary"
            />
            <QuickCard
              to="/places-tester"
              testId="quick-tester"
              title="Test Places API"
              desc="Run a live nearby-search against Google Places to validate your integration."
            />
            <QuickCard
              to="/favorites"
              testId="quick-favorites"
              title="User Favorites"
              desc="View and clean up favorites saved by NearMe app users."
            />
            <QuickCard
              to="/categories"
              testId="quick-categories"
              title="Categories Catalogue"
              desc="Browse the 50+ place categories powering the drawer navigation."
            />
          </div>

          {/* System info */}
          <div className="bg-card border border-border rounded-2xl p-6">
            <div className="text-sm font-bold mb-4 flex items-center gap-2">
              System information
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <InfoRow
                label="MongoDB"
                value={status?.mongo === "ok" ? "Connected" : "Disconnected"}
                ok={status?.mongo === "ok"}
              />
              <InfoRow
                label="Google API Key"
                value={status?.google_places_configured ? "Present" : "Not set"}
                ok={!!status?.google_places_configured}
              />
              <InfoRow label="API endpoint" value={`${API}`} mono />
              <InfoRow
                label="Last checked"
                value={new Date(status?.timestamp || Date.now()).toLocaleString()}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const QuickCard = ({ to, testId, title, desc, tone }) => (
  <Link
    to={to}
    data-testid={testId}
    className={`group block rounded-2xl border p-6 transition-all hover:-translate-y-0.5 ${
      tone === "primary"
        ? "bg-primary text-primary-foreground border-primary shadow-lg shadow-primary/20 hover:shadow-primary/30"
        : "bg-card border-border hover:shadow-md"
    }`}
  >
    <div className="flex items-start justify-between mb-3">
      <div className="text-base font-bold">{title}</div>
      <ArrowUpRight
        className={`w-5 h-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 ${
          tone === "primary" ? "" : "text-muted-foreground"
        }`}
      />
    </div>
    <div className={`text-sm ${tone === "primary" ? "text-primary-foreground/85" : "text-muted-foreground"}`}>
      {desc}
    </div>
  </Link>
);

const InfoRow = ({ label, value, ok, mono }) => (
  <div className="flex items-start justify-between gap-3 py-2 border-b border-border last:border-0">
    <span className="text-muted-foreground">{label}</span>
    <span
      className={`text-right font-medium flex items-center gap-1.5 ${
        mono ? "mono text-xs break-all" : ""
      }`}
    >
      {ok === true && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />}
      {ok === false && <XCircle className="w-3.5 h-3.5 text-amber-600" />}
      {value}
    </span>
  </div>
);
